/**
 * Hybrid storage: uses localStorage as primary, API as sync target.
 * When API is available, data syncs to the server.
 * When API is unavailable, data stays in localStorage and a status indicator is shown.
 *
 * Unified sample storage: reads from all legacy keys for backward compatibility.
 */

const LS_INFLUENT_KEY = 'rb_influent_samples_v2';
const LS_TRAINS_KEY = 'rb_process_trains_v2';
const LS_SITES_KEY = 'rb_customer_sites';
const LS_SITE_PROCESSES_KEY = 'rb_site_processes_v1';
const LS_RESERVOIR_CONFIGS_KEY = 'rb_reservoir_configs_v1';
const LEGACY_SAMPLES_KEY = 'rb_customer_samples'; // from SampleDataTab, ParametersPage

/* ─── Customer Site type ─── */
export type SiteCategory =
  | 'treatment_plant'
  | 'reservoir'
  | 'lake'
  | 'lagoon'
  | 'constructed_wetland'
  | 'pump_station'
  | 'other';

export interface CustomerSite {
  id: string;           // local timestamp string (primary key for localStorage)
  serverId?: number;    // DB integer id — set after API sync
  customerId: string;   // local customer id string
  serverCustomerId?: number; // DB customer id — needed for API save
  name: string;         // e.g. "Main Plant", "Site B"
  address: string;
  postcode: string;
  country: string;      // ISO code e.g. 'AU', 'GB'
  state?: string;       // AU state code (NSW) or UK EA region name
  stateName?: string;   // full state/region name
  region?: string;      // UK EA region or AU state full name
  permitRef?: string;   // discharge permit / licence number
  dischargeRoute?: string;
  siteCategory?: SiteCategory; // type of site — drives UI features and compliance logic
  standard?: string;    // site-level standard override
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/* ─── Site Process Equipment ─── */
export interface SiteProcessItem {
  id: string;           // equipment id from EQUIPMENT_LIBRARY or RESERVOIR_EQUIPMENT
  name: string;
  category: string;
  order: number;        // position in process train
  notes?: string;
  enabled: boolean;
}

export interface SiteProcess {
  id: string;           // local timestamp string
  siteId: string;       // CustomerSite.id
  customerId: string;
  name: string;         // e.g. "Main Treatment Train", "Aeration System A"
  description?: string;
  trainType: 'treatment' | 'aeration' | 'monitoring' | 'dosing' | 'other';
  equipment: SiteProcessItem[];
  createdAt: string;
  updatedAt: string;
}

/* ─── Reservoir / Lake Configuration ─── */
export interface ReservoirConfig {
  id: string;           // siteId — one config per site
  siteId: string;
  customerId: string;
  // Physical characteristics
  volumeMl: number;              // volume in megalitres (ML)
  surfaceAreaHa: number;         // surface area in hectares
  maxDepthM: number;             // maximum depth (m)
  avgDepthM: number;             // mean depth (m)
  catchmentAreaKm2: number;      // catchment area (km²)
  // Hydraulics
  annualInflowMl: number;        // annual inflow (ML/year)
  retentionTimeDays: number;     // theoretical hydraulic retention time (days)
  // Water body classification
  waterBodyType: 'drinking_water_reservoir' | 'irrigation_storage' | 'recreational_lake' | 'ecological_reserve' | 'industrial_storage' | 'stormwater_detention';
  trophicStatus: 'oligotrophic' | 'mesotrophic' | 'eutrophic' | 'hypertrophic' | 'unknown';
  stratification: 'well_mixed' | 'seasonal' | 'permanent';
  // Target compliance (can be stricter than national standard)
  targetTpMgL: number;           // target TP ≤ x mg/L
  targetTnMgL: number;           // target TN ≤ x mg/L
  targetChlaUgL: number;         // target Chlorophyll-a ≤ x µg/L
  targetNtuTurbidity: number;    // target turbidity NTU
  targetDoMgL: number;           // target minimum DO mg/L
  // Aeration
  aerationSystems: AerationSystem[];
  notes?: string;
  updatedAt: string;
}

export interface AerationSystem {
  id: string;
  type: 'surface_aerator' | 'diffused_air' | 'destratifier' | 'solar_aerator' | 'draft_tube_mixer' | 'cascade' | 'fountain';
  name: string;
  powerKw: number;               // installed power (kW)
  count: number;                 // number of units
  saeKgO2PerKwh: number;         // standard aeration efficiency (kg O₂/kWh)
  depthM?: number;               // installation depth (diffused air)
  notes?: string;
  enabled: boolean;
}

/* ─── Reservoir Compliance Matrix ─── */

export type ComplianceStatus = 'not_started' | 'in_progress' | 'compliant' | 'non_compliant' | 'na';
export type ObligationLevel  = 'Mandatory' | 'Required' | 'Advisory';

/**
 * User-editable state for a single compliance item.
 * The immutable template (ref, requirement, standard, etc.) lives in the page.
 */
export interface ComplianceOverride {
  ref: string;
  status: ComplianceStatus;
  evidenceNotes: string;
  responsiblePerson: string;
  targetDate: string;
  completedDate: string;
  updatedAt: string;
}

/** All compliance overrides for one site */
export interface SiteComplianceData {
  siteId: string;
  customerId: string;
  /** keyed by item ref (W1, R2, …) */
  overrides: Record<string, ComplianceOverride>;
  updatedAt: string;
}

const LS_COMPLIANCE_KEY = 'rb_site_compliance_v1';

export function getSiteCompliance(siteId: string): SiteComplianceData | null {
  try {
    const raw: SiteComplianceData[] = JSON.parse(localStorage.getItem(LS_COMPLIANCE_KEY) || '[]');
    return raw.find(r => r.siteId === siteId) ?? null;
  } catch { return null; }
}

export function saveSiteCompliance(data: SiteComplianceData): void {
  try {
    const raw: SiteComplianceData[] = JSON.parse(localStorage.getItem(LS_COMPLIANCE_KEY) || '[]');
    const others = raw.filter(r => r.siteId !== data.siteId);
    localStorage.setItem(LS_COMPLIANCE_KEY, JSON.stringify([{ ...data, updatedAt: new Date().toISOString() }, ...others]));
  } catch { /* ignore */ }
}

/* ─── Site Process CRUD ─── */

export function getLocalSiteProcesses(siteId?: string): SiteProcess[] {
  try {
    const raw: SiteProcess[] = JSON.parse(localStorage.getItem(LS_SITE_PROCESSES_KEY) || '[]');
    if (siteId) return raw.filter(p => p.siteId === siteId);
    return raw;
  } catch { return []; }
}

export function saveSiteProcess(proc: Omit<SiteProcess, 'id' | 'createdAt' | 'updatedAt'>): SiteProcess {
  const all = getLocalSiteProcesses();
  const newProc: SiteProcess = {
    ...proc,
    id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(LS_SITE_PROCESSES_KEY, JSON.stringify([newProc, ...all]));
  return newProc;
}

export function updateSiteProcess(id: string, updates: Partial<SiteProcess>): void {
  const all = getLocalSiteProcesses();
  const updated = all.map(p =>
    p.id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p,
  );
  localStorage.setItem(LS_SITE_PROCESSES_KEY, JSON.stringify(updated));
}

export function deleteSiteProcess(id: string): void {
  const all = getLocalSiteProcesses().filter(p => p.id !== id);
  localStorage.setItem(LS_SITE_PROCESSES_KEY, JSON.stringify(all));
}

/* ─── Reservoir Config CRUD ─── */

export function getReservoirConfig(siteId: string): ReservoirConfig | null {
  try {
    const raw: ReservoirConfig[] = JSON.parse(localStorage.getItem(LS_RESERVOIR_CONFIGS_KEY) || '[]');
    return raw.find(r => r.siteId === siteId) ?? null;
  } catch { return null; }
}

export function saveReservoirConfig(config: ReservoirConfig): void {
  try {
    const raw: ReservoirConfig[] = JSON.parse(localStorage.getItem(LS_RESERVOIR_CONFIGS_KEY) || '[]');
    const others = raw.filter(r => r.siteId !== config.siteId);
    localStorage.setItem(LS_RESERVOIR_CONFIGS_KEY, JSON.stringify([config, ...others]));
  } catch { /* ignore */ }
}

/* ─── Normalized Sample type ─── */
export interface UnifiedSample {
  id: number;
  customerId: number | null;
  siteId?: string | null;   // CustomerSite.id
  userId: number;
  sampleName: string;
  reportNumber: string | null;
  params: Record<string, number>;
  standard: string | null;
  notes: string | null;
  sampleType: string | null;
  linkedSampleId: number | null;
  createdAt: string;
  updatedAt: string;
  _local?: boolean;
  _source?: string; // 'influent' | 'customer'
  dbConfirmed?: boolean;   // true = confirmed saved to server DB; false/undefined = local only
  dbSyncedAt?: string;     // ISO timestamp when DB save was confirmed
}

/* ─── Unified sample reading ─── */

function normalizeSample(raw: any, source: string): UnifiedSample {
  return {
    id: raw.id ?? Date.now(),
    customerId: raw.customerId ?? null,
    siteId: raw.siteId ?? null,
    userId: raw.userId ?? 0,
    sampleName: raw.sampleName || raw.name || 'Untitled',
    reportNumber: raw.reportNumber || raw.report_number || null,
    params: raw.params || raw.parameters || {},
    standard: raw.standard || 'UK_EA',
    notes: raw.notes || null,
    sampleType: raw.sampleType || raw.sample_type || 'influent',
    linkedSampleId: raw.linkedSampleId ?? null,
    createdAt: raw.createdAt || raw.created_at || new Date().toISOString(),
    updatedAt: raw.updatedAt || raw.updated_at || new Date().toISOString(),
    _local: !raw.dbConfirmed,
    _source: source,
    dbConfirmed: raw.dbConfirmed ?? false,
    dbSyncedAt: raw.dbSyncedAt ?? undefined,
  };
}

export function getAllLocalSamples(): UnifiedSample[] {
  const merged = new Map<number, UnifiedSample>();

  // 1. Read from new influent key
  try {
    const raw = JSON.parse(localStorage.getItem(LS_INFLUENT_KEY) || '[]');
    raw.forEach((s: any) => {
      const n = normalizeSample(s, 'influent');
      merged.set(n.id, n);
    });
  } catch { /* ignore */ }

  // 2. Read from legacy customer key
  try {
    const raw = JSON.parse(localStorage.getItem(LEGACY_SAMPLES_KEY) || '[]');
    raw.forEach((s: any) => {
      const n = normalizeSample(s, 'customer');
      if (!merged.has(n.id)) {
        merged.set(n.id, n);
      }
    });
  } catch { /* ignore */ }

  return Array.from(merged.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function saveLocalInfluentSample(sample: Partial<UnifiedSample>): UnifiedSample {
  // Save to new key
  const existing = getLocalInfluentSamples();
  const newSample: UnifiedSample = {
    id: sample.id || Date.now(),
    customerId: sample.customerId ?? null,
    siteId: sample.siteId ?? null,
    userId: sample.userId ?? 0,
    sampleName: sample.sampleName || 'Untitled',
    reportNumber: sample.reportNumber || null,
    params: sample.params || {},
    standard: sample.standard || 'UK_EA',
    notes: sample.notes || null,
    sampleType: sample.sampleType || 'influent',
    linkedSampleId: sample.linkedSampleId ?? null,
    createdAt: sample.createdAt || new Date().toISOString(),
    updatedAt: sample.updatedAt || new Date().toISOString(),
    _local: true,
    _source: 'influent',
    dbConfirmed: sample.dbConfirmed ?? false,
    dbSyncedAt: sample.dbSyncedAt ?? undefined,
  };
  localStorage.setItem(LS_INFLUENT_KEY, JSON.stringify([newSample, ...existing]));

  // Also save to legacy key for backward compatibility (so Customer Data tab sees it)
  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_SAMPLES_KEY) || '[]');
    const legacySample = {
      ...newSample,
      createdAt: new Date(newSample.createdAt),
    };
    localStorage.setItem(LEGACY_SAMPLES_KEY, JSON.stringify([legacySample, ...legacy]));
  } catch { /* ignore */ }

  return newSample;
}

export function getLocalInfluentSamples(): UnifiedSample[] {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_INFLUENT_KEY) || '[]');
    return raw.map((s: any) => normalizeSample(s, 'influent'));
  } catch {
    return [];
  }
}

export function deleteLocalInfluentSample(id: number) {
  const existing = getLocalInfluentSamples().filter((s) => s.id !== id);
  localStorage.setItem(LS_INFLUENT_KEY, JSON.stringify(existing));
}

/** Mark a locally-saved sample as confirmed stored in the server DB. */
export function markSampleDbConfirmed(id: number, serverId?: number): void {
  const existing = getLocalInfluentSamples();
  const updated = existing.map(s =>
    s.id === id
      ? { ...s, dbConfirmed: true, dbSyncedAt: new Date().toISOString(), _local: false,
          ...(serverId ? { id: serverId } : {}) }
      : s
  );
  localStorage.setItem(LS_INFLUENT_KEY, JSON.stringify(updated));
}

/** Returns true if any locally-stored samples have NOT been confirmed in the DB. */
export function hasUnconfirmedSamples(sampleType?: 'influent' | 'effluent'): boolean {
  const all = getLocalInfluentSamples();
  return all.some(s => !s.dbConfirmed && (!sampleType || s.sampleType === sampleType));
}

export function clearLocalInfluentSamples() {
  localStorage.removeItem(LS_INFLUENT_KEY);
}

/* ─── Process Trains ─── */

export interface UnifiedTrain {
  id: number;
  userId: number;
  name: string;
  description: string | null;
  sampleId: number | null;
  customerId: number | null;
  proposalId: string | null;
  equipmentList: any[];
  status: 'draft' | 'in_progress' | 'assessed';
  assessmentResult: any;
  createdAt: string;
  updatedAt: string;
  _local?: boolean;
}

export function getLocalProcessTrains(): UnifiedTrain[] {
  try {
    return JSON.parse(localStorage.getItem(LS_TRAINS_KEY) || '[]');
  } catch {
    return [];
  }
}

export function saveLocalProcessTrain(train: Partial<UnifiedTrain>): UnifiedTrain {
  const existing = getLocalProcessTrains();
  const newTrain: UnifiedTrain = {
    id: train.id || Date.now(),
    userId: train.userId ?? 0,
    name: train.name || 'Untitled Train',
    description: train.description ?? null,
    sampleId: train.sampleId ?? null,
    customerId: (train as any).customerId ?? null,
    proposalId: (train as any).proposalId ?? null,
    equipmentList: train.equipmentList || [],
    status: train.status || 'draft',
    assessmentResult: train.assessmentResult ?? null,
    createdAt: train.createdAt || new Date().toISOString(),
    updatedAt: train.updatedAt || new Date().toISOString(),
    _local: true,
  };
  localStorage.setItem(LS_TRAINS_KEY, JSON.stringify([newTrain, ...existing]));
  return newTrain;
}

export function updateLocalProcessTrain(id: number, updates: Partial<UnifiedTrain>) {
  const existing = getLocalProcessTrains();
  const updated = existing.map((t) =>
    t.id === id ? { ...t, ...updates, updatedAt: new Date().toISOString() } : t
  );
  localStorage.setItem(LS_TRAINS_KEY, JSON.stringify(updated));
}

export function deleteLocalProcessTrain(id: number) {
  const existing = getLocalProcessTrains().filter((t) => t.id !== id);
  localStorage.setItem(LS_TRAINS_KEY, JSON.stringify(existing));
}

export function clearLocalProcessTrains() {
  localStorage.removeItem(LS_TRAINS_KEY);
}

/* ─── Customer Sites ─── */

export function getLocalSites(customerId?: string): CustomerSite[] {
  try {
    const raw: CustomerSite[] = JSON.parse(localStorage.getItem(LS_SITES_KEY) || '[]');
    if (customerId) return raw.filter(s => s.customerId === customerId);
    return raw;
  } catch { return []; }
}

export function saveLocalSite(site: Omit<CustomerSite, 'id' | 'createdAt' | 'updatedAt'>): CustomerSite {
  const existing = getLocalSites();
  const newSite: CustomerSite = {
    ...site,
    id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(LS_SITES_KEY, JSON.stringify([newSite, ...existing]));
  return newSite;
}

export function updateLocalSite(id: string, updates: Partial<CustomerSite>): void {
  const existing = getLocalSites();
  const updated = existing.map(s =>
    s.id === id ? { ...s, ...updates, updatedAt: new Date().toISOString() } : s
  );
  localStorage.setItem(LS_SITES_KEY, JSON.stringify(updated));
}

export function deleteLocalSite(id: string): void {
  const existing = getLocalSites().filter(s => s.id !== id);
  localStorage.setItem(LS_SITES_KEY, JSON.stringify(existing));
}
